import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchBalance } from "../user/UserActions";
import { AppState } from "../RootReducer";
import { LoginStatus } from "../http/Types";
import { setBalanceUpdateFrequency, setToLastBalanceUpdateFrequency } from "../settings/SettingsActions";
import { throttle } from "../user/ui/utils";

export enum BalanceUpdateFrequency {
  STOPPED = "STOPPED",
  FAST = "FAST",
  SLOW = "SLOW",
  INACTIVE = "INACTIVE",
}

const updateIntervals: { [key in BalanceUpdateFrequency]?: number | null } = {
  [BalanceUpdateFrequency.FAST]: 20000,
  [BalanceUpdateFrequency.SLOW]: 60000,
  [BalanceUpdateFrequency.STOPPED]: 60000,
  [BalanceUpdateFrequency.INACTIVE]: null,
};

export const defaultBalanceUpdateFrequency = BalanceUpdateFrequency.FAST;
const VISIBILITY_STATE_HIDDEN = "hidden";
const fiveMinInMillisec = 5 * 60 * 1000;
const inactiveThrottleTimer = 1000;
const fallback = 10000; // Set a default value for the fallback interval

let lastFullFetch = 0; // Timestamp for the last full fetch
let fetchCount = 0; // Count the number of fetches to determine when to do a full fetch
const fetchBalanceWithDefaults = (
  currencies: String[] = [],
  skipBalance: boolean = false
) => fetchBalance(false, false, currencies, skipBalance);

const BalanceUpdater = () => {
  const dispatch = useDispatch();
  const {
    loggedIn,
    errorCount,
    balanceUpdating,
    balanceUpdateFrequency,
    activeCurrency,
  } = useSelector(({ user, settings }: AppState) => ({
    loggedIn: LoginStatus.LOGGED_IN === user.status,
    error: user.balanceError,
    errorCount: user.balanceErrorCount,
    balanceUpdating: user.balanceUpdating,
    balanceUpdateFrequency: settings.balanceUpdateFrequency,
    activeCurrency: user.activeCurrency,
  }));
  const doFullFetch = useCallback(async () => {
    if(lastFullFetch + 10000 > Date.now()) return;
    lastFullFetch = Date.now();
    await dispatch(fetchBalanceWithDefaults([], false) as any);
  }, [dispatch]);

  useEffect(() => {
    if (!loggedIn || balanceUpdateFrequency === BalanceUpdateFrequency.INACTIVE) return;
      try {
        doFullFetch();
      } catch (e) {
        console.log("Failed to update balance", e);
      }
  }, [doFullFetch, balanceUpdateFrequency, loggedIn]);

  useEffect(() => {
    if (
      !loggedIn ||
      balanceUpdateFrequency === BalanceUpdateFrequency.INACTIVE
    ) {
      return;
    }

    const doFetchBalance = async () => {
      const shouldFetchBalance = balanceUpdating || (errorCount ?? 0) < 10;
      if (!shouldFetchBalance) return;

      const hasBeen5Minutes = Date.now() - lastFullFetch >= fiveMinInMillisec;
      const skipBalance =
        balanceUpdateFrequency === BalanceUpdateFrequency.STOPPED;
      const shouldDoFullFetch = hasBeen5Minutes || fetchCount >= 10;

      try {
        if (shouldDoFullFetch && !skipBalance) {
          await doFullFetch();
          lastFullFetch = Date.now();
          fetchCount = 0;
        } else {
          const currency: string[] = activeCurrency ? [activeCurrency] : [];
          dispatch(fetchBalanceWithDefaults(currency, skipBalance) as any);
          fetchCount++;
        }
      } catch (e) {
        console.log("Failed to update balance", e);
      }
    };

    const updateInterval = updateIntervals[balanceUpdateFrequency ?? defaultBalanceUpdateFrequency] || fallback;

    const interval = setInterval(async () => {
      doFetchBalance();
    }, updateInterval);

    return () => {
      clearInterval(interval);
    };
  }, [
    loggedIn,
    errorCount,
    balanceUpdating,
    dispatch,
    balanceUpdateFrequency,
    activeCurrency,
    doFullFetch
  ]);

  // Manages the state when the current browser tab is not active or visible to the user
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === VISIBILITY_STATE_HIDDEN) {
        dispatch(setBalanceUpdateFrequency(BalanceUpdateFrequency.INACTIVE) as any);
      } else {
        dispatch(setToLastBalanceUpdateFrequency() as any);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch]);

  // Manages user activity and inactivity
  useEffect(() => {
    let timeoutId: number;

    const initiateTimeout = () => {
      return window.setTimeout(() => {
        if(BalanceUpdateFrequency.INACTIVE === balanceUpdateFrequency) return;
        dispatch(setBalanceUpdateFrequency(BalanceUpdateFrequency.INACTIVE) as any);
      }, fiveMinInMillisec * 2);
    };

    const resetTimeoutThrottled = throttle(() => {
      if (balanceUpdateFrequency === BalanceUpdateFrequency.INACTIVE) {
        dispatch(setToLastBalanceUpdateFrequency() as any);
      }
      clearTimeout(timeoutId);
      timeoutId = initiateTimeout();
    }, inactiveThrottleTimer);

    const resetTimeout = () => resetTimeoutThrottled();

    window.addEventListener("mousemove", resetTimeout);
    window.addEventListener("keydown", resetTimeout);
    window.addEventListener("scroll", resetTimeout);

    timeoutId = initiateTimeout();

    return () => {
      window.removeEventListener("mousemove", resetTimeout);
      window.removeEventListener("keydown", resetTimeout);
      window.removeEventListener("scroll", resetTimeout);
      clearTimeout(timeoutId);
    };
  }, [dispatch, balanceUpdateFrequency]);

  return null;
};

export default BalanceUpdater;
